import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { City, Country, State } from "country-state-city";
import React, { useEffect, useState } from "react";
import { autocompleteDropdownStyles } from "Constants/Style";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import FileUploadInput from "Common/Components/FileUploadInput/FileUploadInput";
import { useDispatch, useSelector } from "react-redux";
import { fetchLabourById, addLabour, updateLabour } from "slices/Labour/thunk";
import PageContainer from "components/PageContainer/PageContainer";

const SUPPORTED_FORMATS = ["image/jpeg", "image/png", "image/jpg"];

const commonInputStyle = {
  fontSize: "0.7rem",
  minHeight: 38,
  padding: "0.350rem 0.75rem",
};

const thumbStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  objectFit: "cover",
  borderRadius: 4,
  border: "1px solid #ddd",
};

interface LabourFormValues {
  labour_name: string;
  contact_number: string;
  // enable_FIFO: string;
  address: string;
  district: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  remark: string;
  pan_number: string;
  pan_document: File | string | null;
  aadhar_number: string;
  aadhar_document: File | string | null;
}

const commonLabelStyle = { fontSize: "0.8rem", marginBottom: 4 };

const LabourForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  // Redux selectors
  const selectedLabour = useSelector(
    (state: any) => state.labour?.selectedLabour
  );
  const isLoading = useSelector((state: any) => state.labour?.loading);
  const labourError = useSelector((state: any) => state.labour?.error);

  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const [panPreviewVisible, setPanPreviewVisible] = useState(false);
  const [aadharPreviewVisible, setAadharPreviewVisible] = useState(false);

  // Helper function to extract error message from error response
  const getErrorMessage = (error: any): string => {
    if (typeof error === "string") return error;
    if (error?.error) return error.error;
    if (error?.message) return error.message;
    if (error?.response?.data?.error) return error.response.data.error;
    if (error?.response?.data?.message) return error.response.data.message;
    return "Something went wrong";
  };

  // Fetch countries once
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);

    // Auto-select India when creating a new labour (not in edit mode)
    if (!id && countryList.length > 0) {
      const india = countryList.find(
        (c: any) => c.name === "India" || c.isoCode === "IN"
      );
      if (india) {
        formik.setFieldValue("country", india.name);
        // Load states for India
        const indiaStates = State.getStatesOfCountry(india.isoCode);
        setStates(indiaStates);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch labour details if editing
  useEffect(() => {
    if (id && countries.length > 0) {
      dispatch(fetchLabourById(id)).catch((error: any) => {
        const errorMsg = getErrorMessage(error);
        toast.error(errorMsg);
      });
    }
  }, [id, countries, dispatch]);

  // Watch for labour errors from Redux store
  // useEffect(() => {
  //   if (labourError) {
  //     const errorMsg = getErrorMessage(labourError);
  //     toast.error(errorMsg);
  //   }
  // }, [labourError]);

  // Update formik values when selectedLabour changes in Redux store
  useEffect(() => {
    if (selectedLabour && id && countries.length > 0) {
      // Handle nested response structure: response.data.data or response.data
      const response =
        selectedLabour?.data?.data || selectedLabour?.data || selectedLabour;
      if (Object.keys(response).length > 0) {
        // Find country
        const selectedCountry = countries.find(
          (c) => c.name === response.country
        );
        const countryIsoCode = selectedCountry?.isoCode || "";

        // Get states
        const statesOfCountry = State.getStatesOfCountry(countryIsoCode);
        setStates(statesOfCountry);

        // Find state
        const selectedState = statesOfCountry.find(
          (s) => s.name === response.state
        );
        const stateIsoCode = selectedState?.isoCode || "";

        // Get cities
        const citiesOfState = City.getCitiesOfState(
          countryIsoCode,
          stateIsoCode
        );
        setCities(citiesOfState);

        // Set formik values
        formik.setValues({
          labour_name: response.labour_name || "",
          contact_number: response.contact_number || "",
          address: response.address || "",
          district: "", // remove if unused
          city: response.city || "",
          state: response.state || "",
          country: response.country || "",
          pincode: response.pincode || "",
          remark: response.remark || "",
          pan_number: response.pan_number || "",
          pan_document: response.pan_document || null,
          aadhar_number: response.aadhar_number || "",
          aadhar_document: response.aadhar_document || null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // console.log("Selected Labour",selectedLabour)
    // console.log("Pan URL",`${formik.values.pan_document}||||`)
    console.log("Pan URL", encodeURI(formik.values.pan_document as string));
  }, [selectedLabour, id, countries]);

  const formik = useFormik<LabourFormValues>({
    initialValues: {
      labour_name: "",
      contact_number: "",
      // enable_FIFO: "",
      address: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      remark: "",
      pan_number: "",
      pan_document: null,
      aadhar_number: "",
      aadhar_document: null,
    },
    validationSchema: Yup.object({
      labour_name: Yup.string().required("Labour name is required"),
      contact_number: Yup.string()
        .matches(/^\d{10}$/, "Contact number must be exactly 10 digits")
        .required("Contact number is required"),
      address: Yup.string().required("Address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.string().required("State is required"),
      country: Yup.string().required("Country is required"),
      pincode: Yup.string()
        .matches(/^\d{6}$/, "Pincode must be 6 digits")
        .required("Pincode is required"),

      // PAN number
      pan_number: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format")
        .nullable(),

      // PAN document → required if PAN number exists
      pan_document: Yup.mixed().when("pan_number", {
        is: (val: string) => val && val.trim() !== "",
        then: (schema) =>
          schema.required(
            "PAN document is required when PAN number is provided"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

      // Aadhar number
      aadhar_number: Yup.string()
        .nullable()
        .test(
          "len",
          "Aadhar must be exactly 12 digits",
          (val) => !val || val.length === 12 // allow empty/null OR exactly 12 chars
        )
        .matches(/^\d*$/, "Aadhar must contain only digits"),

      // Aadhar document → required if Aadhar number exists
      aadhar_document: Yup.mixed().when("aadhar_number", {
        is: (val: string) => val && val.trim() !== "",
        then: (schema) =>
          schema.required(
            "Aadhar document is required when Aadhaar number is provided"
          ),
        otherwise: (schema) => schema.notRequired(),
      }),

      // remark: Yup.string().required("Remark is required"),

      // pan_number: Yup.string()
      //   .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN number")
      //   .required("PAN number is required"),

      // pan_document: Yup.mixed<File | string>()
      //   .test("fileSize", "File too large (max 2MB)", (value) => {
      //     if (value && typeof value !== "string") {
      //       return value.size <= 2 * 1024 * 1024;
      //     }
      //     return true;
      //   })
      //   .test("fileType", "Unsupported file format", (value) => {
      //     if (value && typeof value !== "string") {
      //       return SUPPORTED_FORMATS.includes(value.type);
      //     }
      //     return true;
      //   })
      //   .nullable(),

      // aadhar_number: Yup.string()
      //   .matches(/^[0-9]{12}$/, "Aadhaar must be 12 digits")
      //   .required("Aadhaar number is required"),

      // aadhar_document: Yup.mixed<File | string>()
      //   .test("fileSize", "File too large (max 2MB)", (value) => {
      //     if (value && typeof value !== "string") {
      //       return value.size <= 2 * 1024 * 1024;
      //     }
      //     return true;
      //   })
      //   .test("fileType", "Unsupported file format", (value) => {
      //     if (value && typeof value !== "string") {
      //       return SUPPORTED_FORMATS.includes(value.type);
      //     }
      //     return true;
      //   })
      //   .nullable(),
    }),

    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        console.log("Submitting values:", values);
        Object.keys(values).forEach((key) => {
          const value = values[key as keyof LabourFormValues];

          // Only append files or new values, skip existing strings for file inputs
          // if (value !== null && value !== undefined) {
            // if (
            //   (key === "pan_document" || key === "aadhar_document") &&
            //   typeof value === "string"
            // ) {
            //   // skip string URLs for existing files
            //   return;
            // }
          // }
          formData.append(key, value as any);
        });

        console.log("formData entries:",formData);

        if (id) {
          await dispatch(updateLabour({ id, formData })).unwrap();
          toast.success("Labour updated successfully");
        } else {
          await dispatch(addLabour(formData)).unwrap();
          toast.success("Labour created successfully");
        }
        navigate("/labour", { state: { clearState: !id } });
      } catch (error: any) {
        const errorMsg = getErrorMessage(error);
        toast.error(errorMsg);
      }
    },
  });

  console.log("Formik Values:", formik.values);
  return (
    <PageContainer>
      <FormHeader
        title={`${id ? "Edit" : "Add"} Labour`}
        firstLabel="Labour"
        secondLabel={id ? "Edit Labour" : "Add Labour"}
        handleArrowClick={() => navigate("/labour")}
      />

      {isLoading && id ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
          {/* First Row */}
          <Row className="mb-4">
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold">
                Labour Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("labour_name")}
                isInvalid={
                  !!formik.errors.labour_name && formik.touched.labour_name
                }
                placeholder="Enter Labour Name"
                size="sm"
                style={commonInputStyle}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.labour_name}
              </Form.Control.Feedback>
            </Col>

            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold">
                Contact No. <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                {...formik.getFieldProps("contact_number")}
                isInvalid={
                  !!formik.errors.contact_number &&
                  formik.touched.contact_number
                }
                placeholder="Enter Contact No."
                size="sm"
                style={commonInputStyle}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.contact_number}
              </Form.Control.Feedback>
            </Col>
          </Row>

          <hr />

          {/* PAN and Aadhar */}
          <Row className="my-4">
            {/* PAN Number */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Group controlId="pan_number">
                <Form.Label>
                  PAN Number
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>
                <Form.Control
                  type="text"
                  {...formik.getFieldProps("pan_number")}
                  isInvalid={
                    formik.touched.pan_number && !!formik.errors.pan_number
                  }
                  placeholder="Enter PAN Number"
                  size="sm"
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.pan_number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* PAN Document */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label>
                PAN Document{" "}
                {formik.values.pan_number && (
                  <span className="text-danger">*</span>
                )}{" "}
              </Form.Label>

              <div className="d-flex align-items-center gap-2">
                <FileUploadInput
                  label=""
                  name="pan_document"
                  formik={formik}
                  compact={true}
                  accept="image/*"
                  // existingFile={true}
                />

                {formik.values.pan_document &&
                  typeof formik.values.pan_document === "string" && (
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                      }}
                      onMouseEnter={() => setPanPreviewVisible(true)}
                      onMouseLeave={() => setPanPreviewVisible(false)}
                    >
                      {/* Fixed button */}
                      <button
                        type="button"
                        style={{
                          width: "95%",
                          height: "95%",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                          fontSize: "1.5rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        📄
                      </button>

                      {/* Hover image preview */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-210px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "200px",
                          height: "200px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          overflow: "hidden",
                          backgroundColor: "#fff",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                          zIndex: 1000,
                          opacity: panPreviewVisible ? 1 : 0,
                          visibility: panPreviewVisible ? "visible" : "hidden",
                          transition: "opacity 0.2s, visibility 0.2s",
                        }}
                      >
                        <img
                          src={encodeURI(formik.values.pan_document as string)}
                          crossOrigin="anonymous"
                          alt="PAN Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>

              {formik.touched.pan_document && formik.errors.pan_document && (
                <div
                  className="invalid-feedback d-block"
                  style={{ fontSize: "12px" }}
                >
                  {formik.errors.pan_document}
                </div>
              )}
            </Col>

            {/* Aadhaar Number */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Group controlId="aadhar_number">
                <Form.Label>
                  Aadhaar Number
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>
                <Form.Control
                  type="text"
                  {...formik.getFieldProps("aadhar_number")}
                  onChange={(e) => {
                    // Keep only digits, max 12
                    const numericValue = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 12);
                    formik.setFieldValue("aadhar_number", numericValue);
                  }}
                  value={formik.values.aadhar_number}
                  maxLength={12}
                  isInvalid={
                    formik.touched.aadhar_number &&
                    !!formik.errors.aadhar_number
                  }
                  placeholder="Enter Aadhaar Number"
                  size="sm"
                  style={commonInputStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.aadhar_number}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Aadhaar Document */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label>
                Aadhaar Document
                {formik.values.aadhar_number && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <FileUploadInput
                  label=""
                  name="aadhar_document"
                  formik={formik}
                  compact={true}
                  accept="image/*"
                  // existingFile={true}
                />

                {formik.values.aadhar_document &&
                  typeof formik.values.aadhar_document === "string" && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                      onMouseEnter={() => setAadharPreviewVisible(true)}
                      onMouseLeave={() => setAadharPreviewVisible(false)}
                    >
                      <button
                        type="button"
                        style={{
                          width: "95%",
                          height: "95%",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          backgroundColor: "#f0f0f0",
                          cursor: "pointer",
                          fontSize: "1.5rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        📄
                      </button>

                      <div
                        style={{
                          position: "absolute",
                          top: "-210px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "200px",
                          height: "200px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          overflow: "hidden",
                          backgroundColor: "#fff",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                          zIndex: 1000,
                          opacity: aadharPreviewVisible ? 1 : 0,
                          visibility: aadharPreviewVisible
                            ? "visible"
                            : "hidden",
                          transition: "opacity 0.2s, visibility 0.2s",
                        }}
                      >
                        <img
                          src={encodeURI(
                            formik.values.aadhar_document as string
                          )}
                          crossOrigin="anonymous"
                          alt="Aadhaar Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </div>
                  )}
              </div>

              {formik.touched.aadhar_document &&
                formik.errors.aadhar_document && (
                  <div
                    className="invalid-feedback d-block"
                    style={{ fontSize: "12px" }}
                  >
                    {formik.errors.aadhar_document as string}
                  </div>
                )}
            </Col>
          </Row>

          <hr />

          {/* Address + Dropdowns */}
          <Row className="mb-4">
            {/* Address */}
            <Col xs={12} sm={6} lg={3} className="mb-0">
              <Form.Label className="fw-semibold mb-1">
                Address <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("address")}
                isInvalid={!!formik.errors.address && formik.touched.address}
                placeholder="Enter Address"
                size="sm"
                style={commonInputStyle}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.address}
              </Form.Control.Feedback>
            </Col>

            {/* Country */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                Country <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name}
                value={
                  countries.find((c) => c.name === formik.values.country) ||
                  null
                }
                onChange={(e, selectedCountry) => {
                  formik.setFieldValue("country", selectedCountry?.name || "");
                  formik.setFieldValue("state", "");
                  formik.setFieldValue("city", "");
                  if (selectedCountry) {
                    setIsLoadingStates(true);
                    const newStates = State.getStatesOfCountry(
                      selectedCountry.isoCode
                    );
                    setStates(newStates);
                    setCities([]);
                    setIsLoadingStates(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Country"
                    size="small"
                    error={!!formik.errors.country && formik.touched.country}
                    helperText={formik.touched.country && formik.errors.country}
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </Col>

            {/* State */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                State <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={states}
                getOptionLabel={(option) => option.name}
                loading={isLoadingStates}
                value={
                  states.find((s) => s.name === formik.values.state) || null
                }
                onChange={(e, selectedState) => {
                  const selectedCountry = countries.find(
                    (c) => c.name === formik.values.country
                  );
                  formik.setFieldValue("state", selectedState?.name || "");
                  formik.setFieldValue("city", "");
                  if (selectedCountry && selectedState) {
                    setIsLoadingCities(true);
                    const newCities = City.getCitiesOfState(
                      selectedCountry.isoCode,
                      selectedState.isoCode
                    );
                    setCities(newCities);
                    setIsLoadingCities(false);
                  }
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select State"
                    size="small"
                    error={!!formik.errors.state && formik.touched.state}
                    helperText={formik.touched.state && formik.errors.state}
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                disabled={!formik.values.country}
              />
            </Col>

            {/* City */}
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold" style={commonLabelStyle}>
                City <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={cities}
                getOptionLabel={(option) => option.name}
                loading={isLoadingCities}
                value={
                  cities.find((c) => c.name === formik.values.city) || null
                }
                onChange={(e, selectedCity) => {
                  formik.setFieldValue("city", selectedCity?.name || "");
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select City"
                    size="small"
                    error={!!formik.errors.city && formik.touched.city}
                    helperText={formik.touched.city && formik.errors.city}
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
                disabled={!formik.values.state}
              />
            </Col>
          </Row>

          {/* Pincode + Remark */}
          <Row className="mb-4">
            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold">
                Pincode <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Pincode"
                {...formik.getFieldProps("pincode")}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  formik.setFieldValue("pincode", numericValue);
                }}
                isInvalid={!!formik.errors.pincode && formik.touched.pincode}
                size="sm"
                style={commonInputStyle}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.pincode}
              </Form.Control.Feedback>
            </Col>

            <Col xs={12} sm={6} lg={3} className="mb-3">
              <Form.Label className="fw-semibold">Remark</Form.Label>
              <Form.Control
                type="text"
                {...formik.getFieldProps("remark")}
                isInvalid={!!formik.errors.remark && formik.touched.remark}
                placeholder="-"
                size="sm"
                style={commonInputStyle}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.remark}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Actions */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelBackButton to={-1} />
            {/* <Button variant="primary" type="submit">
                Submit
              </Button> */}

            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={!!id} />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default LabourForm;
